import React, { useState } from 'react';

interface FlowStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'current' | 'completed' | 'error';
  details?: string;
}

export default function BettingFlowVisual() {
  const [currentStep, setCurrentStep] = useState(0);

  const steps: FlowStep[] = [
    {
      id: 'select-market',
      title: '1. Select Market',
      description: 'User clicks "Place Bet" on a market',
      status: currentStep === 0 ? 'current' : currentStep > 0 ? 'completed' : 'pending',
      details: 'Market: "Will Brian ship 5+ features?"'
    },
    {
      id: 'choose-option',
      title: '2. Choose Option',
      description: 'Select YES or NO option',
      status: currentStep === 1 ? 'current' : currentStep > 1 ? 'completed' : 'pending',
      details: 'Selected: YES (Currently 75%)'
    },
    {
      id: 'enter-amount',
      title: '3. Enter Amount',
      description: 'Input bet amount',
      status: currentStep === 2 ? 'current' : currentStep > 2 ? 'completed' : 'pending',
      details: 'Amount: $100 (Balance: $1000)'
    },
    {
      id: 'calculate-impact',
      title: '4. Calculate Impact',
      description: 'Show probability change and potential payout',
      status: currentStep === 3 ? 'current' : currentStep > 3 ? 'completed' : 'pending',
      details: 'New probability: 82% (+7%), Payout if win: $122'
    },
    {
      id: 'confirm-bet',
      title: '5. Confirm Bet',
      description: 'Review and confirm all details',
      status: currentStep === 4 ? 'current' : currentStep > 4 ? 'completed' : 'pending',
      details: 'Shares to receive: 83.33'
    },
    {
      id: 'submit-api',
      title: '6. Submit to API',
      description: 'Send bet to backend',
      status: currentStep === 5 ? 'current' : currentStep > 5 ? 'completed' : 'pending',
      details: 'POST /api/bets'
    },
    {
      id: 'update-ui',
      title: '7. Update UI',
      description: 'Update balance and market',
      status: currentStep === 6 ? 'current' : currentStep > 6 ? 'completed' : 'pending',
      details: 'New balance: $900, Market updated'
    },
    {
      id: 'show-success',
      title: '8. Show Success',
      description: 'Display confirmation',
      status: currentStep === 7 ? 'current' : currentStep > 7 ? 'completed' : 'pending',
      details: '✅ Bet placed successfully!'
    }
  ];

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'current': return 'bg-blue-500 animate-pulse';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'current': return '🔄';
      case 'error': return '❌';
      default: return '⭕';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-gray-800 rounded-2xl shadow-2xl p-8">
          <h1 className="text-3xl font-bold mb-6">💰 Betting Flow Visualization</h1>
          <p className="text-gray-400 mb-8">
            Track the complete betting journey from market selection to success confirmation
          </p>

          {/* Flow Steps */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`bg-gray-700 rounded-lg p-6 border-2 transition-all ${
                  step.status === 'current'
                    ? 'border-blue-500 shadow-lg shadow-blue-500/20'
                    : step.status === 'completed'
                    ? 'border-green-500'
                    : 'border-gray-600'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-2xl">{getStepIcon(step.status)}</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{step.title}</h3>
                    <p className="text-gray-400 text-sm mb-2">{step.description}</p>
                    {step.details && (
                      <div className="bg-gray-800 rounded p-2 text-xs font-mono text-green-400">
                        {step.details}
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress indicator */}
                {index < steps.length - 1 && (
                  <div className="mt-4 flex justify-center">
                    <div className={`w-1 h-8 ${
                      step.status === 'completed' ? 'bg-green-500' : 'bg-gray-600'
                    }`} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:opacity-50 rounded-lg transition-colors"
            >
              ← Previous
            </button>
            <button
              onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
              disabled={currentStep === steps.length - 1}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:opacity-50 rounded-lg transition-colors"
            >
              Next →
            </button>
            <button
              onClick={() => setCurrentStep(0)}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Current State Display */}
          <div className="mt-8 p-6 bg-gray-700 rounded-lg">
            <h3 className="font-bold mb-4">Current State</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Step:</span>{' '}
                <span className="font-bold">{currentStep + 1} of {steps.length}</span>
              </div>
              <div>
                <span className="text-gray-400">Status:</span>{' '}
                <span className="font-bold text-blue-400">
                  {steps[currentStep]?.status || 'pending'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Next Action:</span>{' '}
                <span className="font-bold">
                  {currentStep < steps.length - 1 ? steps[currentStep + 1]?.title : 'Complete!'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Implementation:</span>{' '}
                <span className="font-bold text-yellow-500">Not Started</span>
              </div>
            </div>
          </div>

          {/* Implementation Checklist */}
          <div className="mt-8 p-6 bg-gray-700 rounded-lg">
            <h3 className="font-bold mb-4">📋 Implementation Checklist</h3>
            <div className="space-y-2">
              {[
                'BetModal component',
                'Option selector UI',
                'Amount input with validation',
                'Probability calculator',
                'API integration',
                'Balance update logic',
                'Success/error handling',
                'Mobile responsive design'
              ].map((item, index) => (
                <label key={index} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-green-500 rounded"
                    disabled
                  />
                  <span className="text-gray-300">{item}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-8 p-6 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <h3 className="font-bold text-blue-400 mb-2">💡 Implementation Tips</h3>
          <ul className="text-sm text-gray-300 space-y-1">
            <li>• Start with the BetModal component and work through each step</li>
            <li>• Use the test panel to verify each feature as you build it</li>
            <li>• Implement optimistic UI updates for better UX</li>
            <li>• Add loading states between each major step</li>
            <li>• Test with edge cases (insufficient balance, network errors)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}